/** What a figure rests on, as a chip beside the figure: Estimate, Metered, Supplier-stated basis or
 * Imported, no document - ADR-0212 follow-up (P15).
 *
 * **The word is the server's** (`inventory/basis.py`, `FigureOut.basis` and `ActivityOut.basis`):
 * derived on read from the grade, the origin, whether a document is cited and whether a supplier
 * stated it. This component only draws it, so a register row and a record header cannot disagree,
 * and it has no way to decide a basis. A value it does not know draws nothing rather than a guess
 * (`isBasis` is the gate), and the chip is never the only mark of quality: the grade stays beside it.
 *
 * A shape and a word, never colour alone (design rule 4): `StatusChip` carries both. The one-line
 * tooltip is the chip's `title` and is also in the accessible name, because a title alone is not
 * reached by touch or by every screen reader.
 */

import { CheckCircle, FileArrowUp, Handshake, WarningCircle } from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";

import { BASIS_TIP, BASIS_WORD, isBasis } from "./basis";
import type { Basis } from "./basis";
import { StatusChip } from "./StatusChip";
import type { ChipTone } from "./StatusChip";

const TONE: Readonly<Record<Basis, ChipTone>> = {
  metered: "ok",
  estimate: "warn",
  supplier_stated: "info",
  imported_no_document: "neutral",
};

const GLYPH: Readonly<Record<Basis, Icon>> = {
  metered: CheckCircle,
  estimate: WarningCircle,
  supplier_stated: Handshake,
  imported_no_document: FileArrowUp,
};

export function BasisChip({
  basis,
  testId = "basis-chip",
}: {
  /** The server's word. Anything else draws nothing. */
  basis: string | undefined | null;
  testId?: string;
}) {
  if (!isBasis(basis)) return null;
  return (
    <span data-basis={basis} data-testid={testId}>
      <StatusChip tone={TONE[basis]} size="sm" icon={GLYPH[basis]} title={BASIS_TIP[basis]}>
        {BASIS_WORD[basis]}
      </StatusChip>
      {/* A sibling of the chip, never inside it: the tip is read straight after the word, and the
          chip's own text stays what is drawn. Inside, its unwrapped text spreads far past the chip, and
          a reader of the chip's text boxes (a contrast measurement) sampled whatever lay under it. */}
      <span className="visuallyHidden">. {BASIS_TIP[basis]}</span>
    </span>
  );
}
